import { useEffect } from 'react'

const MOBILE_MEDIA_QUERY = '(max-width: 899px)'
const VIEWPORT_HEIGHT_CSS_VAR = '--visual-viewport-height'
const VIEWPORT_OFFSET_TOP_CSS_VAR = '--visual-viewport-offset-top'

/**
 * Keeps #root exactly covering the visible viewport on mobile (TEC-19134).
 *
 * iOS Safari ignores `interactive-widget=resizes-content`: the on-screen keyboard
 * overlays the layout viewport instead of resizing it, and Safari pans the page to
 * reveal the focused input. With a `position: fixed` #root this can leave the bottom
 * of the layout (continue button) cut off with no way to scroll to it.
 *
 * Both the size AND the origin have to follow the visual viewport. A fixed element is
 * positioned against the LAYOUT viewport, so while Safari is panned by `offsetTop` a
 * #root anchored at top:0 ends `offsetTop` pixels short of the visible area — the grey
 * strip users saw above the keyboard. Mirroring both values keeps the two in lockstep.
 *
 * The app's html/body are `overflow: hidden`, so the document itself never scrolls:
 * the pan is expressed purely as `offsetTop` and is compensated here geometrically
 * rather than by fighting Safari with window.scrollTo.
 */
export const useVisualViewportHeight = () => {
    useEffect(() => {
        const visualViewport = window.visualViewport
        if (!visualViewport) return

        const clear = () => {
            document.documentElement.style.removeProperty(VIEWPORT_HEIGHT_CSS_VAR)
            document.documentElement.style.removeProperty(VIEWPORT_OFFSET_TOP_CSS_VAR)
        }

        const syncViewport = () => {
            const isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches
            const isPinchZoomed = visualViewport.scale !== 1

            if (!isMobile || isPinchZoomed) {
                clear()
                return
            }

            document.documentElement.style.setProperty(
                VIEWPORT_HEIGHT_CSS_VAR,
                `${visualViewport.height}px`,
            )
            document.documentElement.style.setProperty(
                VIEWPORT_OFFSET_TOP_CSS_VAR,
                `${visualViewport.offsetTop}px`,
            )
        }

        syncViewport()
        visualViewport.addEventListener('resize', syncViewport)
        // Safari pans the layout viewport without resizing it (e.g. focus moving
        // between inputs while the keyboard stays open) — that only fires scroll
        visualViewport.addEventListener('scroll', syncViewport)

        return () => {
            visualViewport.removeEventListener('resize', syncViewport)
            visualViewport.removeEventListener('scroll', syncViewport)
            clear()
        }
    }, [])
}
